import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InvoiceDialog } from "@/features/bookings/InvoiceDialog";
import { CustomerEditDialog } from "@/features/customers/CustomerEditDialog";
import { CustomerLinkDialog } from "@/features/customers/CustomerLinkDialog";
import { get, patch } from "@/lib/api";
import { date, eur, today } from "@/lib/format";
import { relativeDays } from "@/lib/relative";
import type { CompanyDetail, CompanyStatus, CustomersOutput } from "@/lib/types";
import { EmailAddresses } from "./EmailAddresses";
import { sevdeskLink, STATUS_LABEL } from "./status";

/** Website link with inline set / change / clear. The server normalises ("firma.at" → "https://firma.at") and rejects non-http(s). */
function Website({ website, saving, onSave }: { website: string | null; saving: boolean; onSave: (website: string | null, done: () => void) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const done = () => setEditing(false);
  if (!editing) {
    return website ? (
      <span className="flex items-center">
        <Button variant="ghost" size="sm" asChild>
          <a href={website} target="_blank" rel="noreferrer"><ExternalLink />{website.replace(/^https?:\/\/(www\.)?/i, "")}</a>
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label="Website ändern" onClick={() => { setValue(website); setEditing(true); }}><Pencil /></Button>
      </span>
    ) : <Button variant="ghost" size="sm" onClick={() => { setValue(""); setEditing(true); }}><Plus />Website</Button>;
  }
  return (
    <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); onSave(value.trim() || null, done); }}>
      <Input aria-label="Website" placeholder="firma.at" inputMode="url" autoComplete="off" spellCheck={false} data-1p-ignore autoFocus
        className="h-8 w-52" value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") done(); }} />
      <Button type="submit" variant="ghost" size="icon-sm" aria-label="Website speichern" disabled={saving}><Check /></Button>
      {website && <Button type="button" variant="ghost" size="icon-sm" aria-label="Website entfernen" disabled={saving} onClick={() => onSave(null, done)}><Trash2 /></Button>}
      <Button type="button" variant="ghost" size="icon-sm" aria-label="Abbrechen" onClick={done}><X /></Button>
    </form>
  );
}

function SevdeskLinkText({ c }: { c: CompanyDetail }) {
  // Same list the Firmen page uses; the header only needs this company's sevDesk name.
  const sev = useQuery({ queryKey: ["customers"], queryFn: () => get<CustomersOutput>("/api/customers") });
  const sevdeskName = sev.data?.customers.find((x) => x.id === c.id)?.sevdeskName ?? null;
  const state = sevdeskLink({ sevdeskContactId: c.sevdeskContactId, sevdeskName }, sev.isSuccess && sev.data.warnings.length === 0);
  if (state === "missing") {
    return <span className="text-sm text-destructive" title="Der verknüpfte Kontakt existiert in sevDesk nicht mehr">sevDesk-Kontakt {c.sevdeskContactId} fehlt</span>;
  }
  return (
    <span className="max-w-64 truncate text-sm text-muted-foreground" title={sevdeskName ?? undefined}>
      {state === "named" ? `sevDesk: ${sevdeskName}` : `sevDesk-Kontakt #${c.sevdeskContactId}`}
    </span>
  );
}

export function CompanyHeader({ c }: { c: CompanyDetail }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const [domain, setDomain] = useState("");
  const asCustomer = { id: c.id, name: c.name, defaultRate: c.defaultRate, sevdeskContactId: c.sevdeskContactId, sevdeskName: null };

  const save = useMutation({
    mutationFn: (body: { status?: CompanyStatus; domains?: string[]; website?: string | null }) => patch<CompanyDetail>(`/api/companies/${c.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company", c.id] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] }); // status changes move companies in and out of "Lange kein Kontakt"
    },
    onError: (e) => toast.error(e.message),
  });
  const addDomain = (e: FormEvent) => {
    e.preventDefault();
    if (domain.trim()) save.mutate({ domains: [...c.domains, domain.trim()] }, { onSuccess: () => setDomain("") });
  };
  const f = c.figures;

  return (
    <section className="space-y-5" aria-label="Stammdaten">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={c.status} onValueChange={(v) => save.mutate({ status: v as CompanyStatus })}>
          <SelectTrigger className="w-36" aria-label="Status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_LABEL) as CompanyStatus[]).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Website website={c.website} saving={save.isPending} onSave={(website, done) => save.mutate({ website }, { onSuccess: done })} />
        {c.sevdeskContactId === null
          ? <Button variant="outline" size="sm" onClick={() => setLinking(true)}>Mit sevDesk verknüpfen…</Button>
          : <SevdeskLinkText c={c} />}
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setEditing(true)}>Name & Stundensatz…</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2" aria-label="Domains">
        <span className="text-sm text-muted-foreground">Domains</span>
        {c.domains.map((d) => (
          <Badge key={d} variant="secondary" className="gap-1 pr-1">
            {d}
            <button type="button" aria-label={`Domain ${d} entfernen`} className="rounded-sm p-0.5 hover:bg-background"
              onClick={() => save.mutate({ domains: c.domains.filter((x) => x !== d) })}><X className="size-3" /></button>
          </Badge>
        ))}
        {c.domainSuggestion && (
          <Button variant="outline" size="xs" onClick={() => save.mutate({ domains: [c.domainSuggestion!] })}>
            <Plus />{c.domainSuggestion} übernehmen
          </Button>
        )}
        <form onSubmit={addDomain} className="flex items-center gap-1">
          <Input aria-label="Domain hinzufügen" placeholder="firma.at" autoComplete="off" spellCheck={false} data-1p-ignore
            className="h-8 w-36" value={domain} onChange={(e) => setDomain(e.target.value)} />
          <Button type="submit" variant="ghost" size="icon-sm" aria-label="Domain hinzufügen" disabled={!domain.trim() || save.isPending}><Plus /></Button>
        </form>
      </div>

      <EmailAddresses c={c} />

      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl p-4 ring-1 ring-border">
          <dt className="text-sm text-muted-foreground">Offen zu verrechnen</dt>
          <dd className="text-2xl font-semibold tabular-nums">{eur(f.openEur)}</dd>
          {f.openEntryIds.length > 0 && <Button variant="outline" size="xs" className="mt-2" onClick={() => setInvoicing(true)}>Rechnung…</Button>}
        </div>
        <div className="rounded-xl p-4 ring-1 ring-border">
          <dt className="text-sm text-muted-foreground">Umsatz gesamt</dt>
          <dd className="text-2xl font-semibold tabular-nums">{eur(f.revenueEur)}</dd>
        </div>
        <div className="rounded-xl p-4 ring-1 ring-border">
          <dt className="text-sm text-muted-foreground">Letzte Rechnung</dt>
          <dd className="text-sm tabular-nums">
            {f.lastInvoice
              ? <>{f.lastInvoice.url ? <a href={f.lastInvoice.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">{f.lastInvoice.number ?? "Rechnung"}</a> : f.lastInvoice.number ?? "Rechnung"}
                  <span className="block text-muted-foreground">{date(f.lastInvoice.date)} · {eur(f.lastInvoice.totalEur)}</span></>
              : <span className="text-muted-foreground">noch keine</span>}
          </dd>
        </div>
        <div className="rounded-xl p-4 ring-1 ring-border">
          <dt className="text-sm text-muted-foreground">Letzter Kontakt</dt>
          <dd className="text-2xl font-semibold tabular-nums">{relativeDays(c.lastContact, today())}</dd>
        </div>
      </dl>

      <CustomerEditDialog customer={editing ? asCustomer : null} onClose={() => setEditing(false)} />
      <CustomerLinkDialog customer={linking ? asCustomer : null} onClose={() => setLinking(false)} />
      <InvoiceDialog entryIds={f.openEntryIds} open={invoicing} onClose={() => setInvoicing(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ["company", c.id] })} />
    </section>
  );
}
